import { assertPrivateSessionLifetime, type PrivateSessionLifetime } from "@/lib/api/private-session";
import type { SimulatorProviderId } from "../types/simulator.types";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { usePrivateMutation } from "@/lib/query/core/use-private-mutation";
import { invalidateAdminOrders } from "@/lib/query/core/invalidation";
import { queryKeys } from "@/lib/query/core/query-keys";
import type { RealSimulatorInput, RealSimulatorPreview, RealSimulatorRun, RealSimulatorRunInput, RealSimulatorOrders } from "../types/real-simulator.types";
const endpoint = "/admin/integration-simulator/real";
async function readOrSend<T extends RealSimulatorPreview>(path: string, input: RealSimulatorInput) {
  const result = await apiJsonClient<T>(`${endpoint}/${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(input) });
  if (result.mode !== "real" || result.provider !== input.provider || result.scenario !== input.scenario || result.effective?.eventId !== input.eventId || result.effective?.eventAt !== input.eventAt || !Array.isArray(result.requests) || !Array.isArray(result.requiredFields) || !Array.isArray(result.blockedReasons) || typeof result.ready !== "boolean" || (result.expectedRevision !== null && typeof result.expectedRevision !== "string") || !Array.isArray(result.expected) || !Array.isArray(result.limits) || !result.effective.providerData || typeof result.effective.providerData !== "object") throw new Error("Unexpected real webhook response");
  if (path === "run") {
    const outcome = result as unknown as RealSimulatorRun;
    if (typeof outcome.uncertain !== "boolean" || typeof outcome.passed !== "boolean" || !Array.isArray(outcome.checks) || (outcome.acceptance !== null && (typeof outcome.acceptance?.httpStatus !== "number" || typeof outcome.acceptance.accepted !== "boolean"))) throw new Error("Unexpected real callback outcome");
  }
  return result;
}
export function useRealSimulatorPreview() {
  return usePrivateMutation({ retry: false, mutationFn: (input: RealSimulatorInput) => readOrSend<RealSimulatorPreview>("preview", input) });
}
function useRefreshResolvedOrder() {
  const client = useQueryClient();
  return (result: RealSimulatorPreview) => {
    if (result.storeId) void invalidateAdminOrders(client, result.storeId).catch(() => undefined);
    void client.invalidateQueries({ queryKey: queryKeys.admin.integrationLogs() }).catch(() => undefined);
  };
}
export function useRealSimulatorState() {
  const refresh = useRefreshResolvedOrder();
  return usePrivateMutation({ retry: false, mutationFn: (input: RealSimulatorInput) => readOrSend<RealSimulatorPreview>("state", input), onSuccess: refresh });
}
export function useRealSimulatorRun() {
  const refresh = useRefreshResolvedOrder();
  return usePrivateMutation({ retry: false, mutationFn: (input: RealSimulatorRunInput) => readOrSend<RealSimulatorRun>("run", input), onSuccess: refresh });
}

export function useRealSimulatorOrders(provider: SimulatorProviderId | undefined, search: string, lifetime: PrivateSessionLifetime) {
  return useInfiniteQuery({
    queryKey: ["admin", "integration-simulator", "orders", lifetime.generation, provider, search],
    enabled: Boolean(provider),
    initialPageParam: 0,
    queryFn: async ({ signal, pageParam }) => {
      assertPrivateSessionLifetime(lifetime);
      const params = new URLSearchParams({ provider: provider!, search, limit: "20", offset: String(pageParam) });
      const result = await apiJsonClient<RealSimulatorOrders>(`${endpoint}/orders?${params}`, { signal });
      assertPrivateSessionLifetime(lifetime);
      if (!Array.isArray(result.items) || typeof result.hasMore !== "boolean" || result.items.some(item =>
        !item || typeof item.orderCode !== "string" || typeof item.storeId !== "string" || typeof item.storeName !== "string" ||
        typeof item.status !== "string" || typeof item.createdAt !== "string" || !Number.isFinite(Date.parse(item.createdAt)) ||
        typeof item.totalAmount !== "number" || !Number.isFinite(item.totalAmount) || !/^[A-Z]{3}$/.test(item.currency) ||
        (item.trackingNumber !== null && typeof item.trackingNumber !== "string")
      )) throw new Error("Invalid real order list");
      return result;
    },
    getNextPageParam: (lastPage, _pages, lastOffset) => lastPage.hasMore && lastOffset < 10_000 ? lastOffset + 20 : undefined,
  });
}
